import { atom, getDefaultStore } from 'jotai';
import { MESSAGE_LIMITS } from '@twinden/shared';
import type {
  Message,
  Conversation,
  UserId,
  WsGatheringRingPayload,
} from '@twinden/shared';
import type { ReplySnapshot } from '../components/chat/messageUtils';
import type { ChatConversation } from '../components/chat/ConversationDialogs';
import type { BlockedPerson } from '../lib/api';

// Conversations
export const conversationsAtom = atom<Conversation[]>([]);

// The home's chat list per family (M66, D248): a return to the home shows
// it at once. Cleared on sign-out (clearAuthAtom) and dropped for a family
// on leaving or deleting it: the next person on a shared phone must not see
// it (R1-1).
export const conversationsByFamilyAtom = atom<
  Record<string, ChatConversation[]>
>({});

export const activeConversationIdAtom = atom<string | null>(null);

// People I blocked (M58, D208), with their names for Settings. null until
// loaded. Direct chats with them stay out of the chat list.
export const blocksAtom = atom<BlockedPerson[] | null>(null);
export const blockedIdsAtom = atom(
  (get) => new Set((get(blocksAtom) ?? []).map((block) => block.user.id))
);

export const activeConversationAtom = atom((get) => {
  const conversations = get(conversationsAtom);
  const activeId = get(activeConversationIdAtom);
  return conversations.find((c) => c.id === activeId) ?? null;
});

// Messages (keyed by conversation ID)
export const messagesAtom = atom<Record<string, Message[]>>({});

export const activeMessagesAtom = atom((get) => {
  const activeId = get(activeConversationIdAtom);
  const messages = get(messagesAtom);
  return activeId ? (messages[activeId] ?? []) : [];
});

// Typing indicators (keyed by conversation ID, value is array of user IDs)
export const typingUsersAtom = atom<Record<string, UserId[]>>({});

export const activeTypingUsersAtom = atom((get) => {
  const activeId = get(activeConversationIdAtom);
  const typing = get(typingUsersAtom);
  return activeId ? (typing[activeId] ?? []) : [];
});

// Online users
export const onlineUsersAtom = atom<Set<UserId>>(new Set<UserId>());

// Actions
export const addMessageAtom = atom(
  null,
  (
    get,
    set,
    { conversationId, message }: { conversationId: string; message: Message }
  ) => {
    const messages = get(messagesAtom);
    const conversationMessages = messages[conversationId] ?? [];
    // Delivery is at-least-once (reconnects, several sockets): keep one copy.
    // Your own message:sent (D206) can also match by clientId (D32).
    const clientId = (message as { clientId?: string | null }).clientId;
    if (
      conversationMessages.some(
        (existing) =>
          existing.id === message.id ||
          (clientId &&
            (existing as { clientId?: string | null }).clientId === clientId)
      )
    ) {
      return;
    }

    set(messagesAtom, {
      ...messages,
      [conversationId]: [...conversationMessages, message],
    });
  }
);

/** Messages that disappeared (D238) leave the live store too. */
export const removeMessagesAtom = atom(
  null,
  (
    get,
    set,
    {
      conversationId,
      messageIds,
    }: { conversationId: string; messageIds: string[] }
  ) => {
    const messages = get(messagesAtom);
    const current = messages[conversationId];
    if (!current) return;
    const gone = new Set(messageIds);
    const kept = current.filter((message) => !gone.has(message.id));
    if (kept.length === current.length) return;
    set(messagesAtom, { ...messages, [conversationId]: kept });
  }
);

export const setMessagesAtom = atom(
  null,
  (
    get,
    set,
    {
      conversationId,
      messages: newMessages,
    }: { conversationId: string; messages: Message[] }
  ) => {
    const messages = get(messagesAtom);

    set(messagesAtom, {
      ...messages,
      [conversationId]: newMessages,
    });
  }
);

// Typing indicators expire on their own (M20, D24): a sender that closes the
// tab mid-sentence never sends `false`, and "typing…" must not stick.
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

function withTyping(
  typing: Record<string, UserId[]>,
  conversationId: string,
  userId: UserId,
  isTyping: boolean
): Record<string, UserId[]> {
  const current = typing[conversationId] ?? [];
  const updated = isTyping
    ? current.includes(userId)
      ? current
      : [...current, userId]
    : current.filter((id) => id !== userId);
  return updated === current
    ? typing
    : { ...typing, [conversationId]: updated };
}

export const setTypingAtom = atom(
  null,
  (
    get,
    set,
    {
      conversationId,
      userId,
      isTyping,
    }: { conversationId: string; userId: UserId; isTyping: boolean }
  ) => {
    const timerKey = `${conversationId}:${userId}`;
    clearTimeout(typingTimers.get(timerKey));
    typingTimers.delete(timerKey);

    set(
      typingUsersAtom,
      withTyping(get(typingUsersAtom), conversationId, userId, isTyping)
    );

    if (isTyping) {
      typingTimers.set(
        timerKey,
        setTimeout(() => {
          typingTimers.delete(timerKey);
          const store = getDefaultStore();
          store.set(
            typingUsersAtom,
            withTyping(
              store.get(typingUsersAtom),
              conversationId,
              userId,
              false
            )
          );
        }, MESSAGE_LIMITS.TYPING_EXPIRE_MS)
      );
    }
  }
);

export const setUserOnlineAtom = atom(
  null,
  (get, set, { userId, online }: { userId: UserId; online: boolean }) => {
    const onlineUsers = get(onlineUsersAtom);
    const newSet = new Set(onlineUsers);

    if (online) {
      newSet.add(userId);
    } else {
      newSet.delete(userId);
    }

    set(onlineUsersAtom, newSet);
  }
);

// The conversation open in the chat view, or null. Phones show it
// full-screen (the app shell hides its header and tabs), and alerts stay
// quiet for messages the user is already looking at.
export const openConversationIdAtom = atom<string | null>(null);

// A shared link or text waiting in a chat's composer (Share to Twinden, D180):
// the composer takes it when that chat opens, and it's gone after.
export const shareDraftAtom = atom<{
  conversationId: string;
  text: string;
} | null>(null);

// Attachments on their way up (issue #2), shown as pending bubbles at the end
// of their conversation. Driven by lib/attachments.ts.
/** One tile of an album on its way up (M60, D219). */
export interface PendingAlbumItem {
  kind: 'image' | 'gif' | 'video';
  previewUrl: string | null;
  /** 0-1 of this file's bytes sent. */
  progress: number;
  /** Uploaded; a retry skips it. */
  done: boolean;
}

export interface PendingUpload {
  id: string;
  conversationId: string;
  kind: 'image' | 'gif' | 'video' | 'voice' | 'file' | 'album';
  /** An album's tiles, in order (M60). */
  items?: PendingAlbumItem[];
  fileName: string;
  fileSize: number;
  previewUrl: string | null;
  width?: number;
  height?: number;
  /** 0-1 of bytes sent. */
  progress: number;
  status: 'uploading' | 'sending' | 'failed';
  error?: string;
  /** Lost the connection and trying again by itself (D134). */
  retrying?: boolean;
  /** Failed for a reason that may clear up: tried again when back online. */
  autoRetry?: boolean;
}

export const pendingUploadsAtom = atom<PendingUpload[]>([]);

// A lone photo's caption whose photo was cancelled (M60, D219): the chat's
// message box takes it back, so it isn't lost.
export const returnedCaptionAtom = atom<{
  conversationId: string;
  text: string;
} | null>(null);

// Text messages on their way out (M23, D31–D34): shown at once, retried when
// the connection comes back, kept on the device until confirmed. Driven by
// lib/outbox.ts.
/** A request ready to go as-is, sealed when encrypted (M29, D68). */
export interface QueuedRequest {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: Record<string, unknown>;
}

export interface OutboxEntry {
  /** For a send, also the message's clientId on the server (D32). */
  clientId: string;
  conversationId: string;
  /** A new message, or an edit or reaction made offline (M29, D69). */
  kind?: 'send' | 'edit' | 'react';
  /** The message an edit or reaction is for. */
  messageId?: string;
  /** That message's own clientId, which an encrypted edit is sealed under. */
  messageClientId?: string;
  /** A reaction's final state: an emoji, or null for none (D69). */
  emoji?: string | null;
  /** The text of a new message or edit. */
  content: string;
  replyToId?: string;
  /** What the pending row quotes. */
  replyTo?: ReplySnapshot | null;
  /** Who it @mentions, declared for encrypted messages (M28, D61). */
  mentions?: string[];
  mentionsEveryone?: boolean;
  createdAt: string;
  status: 'sending' | 'failed';
  /** The server refused it (4xx): not retried automatically (D33). */
  rejected?: boolean;
  /**
   * The server asked this app to update (HTTP 426, #82): kept here, not
   * retried, shown as "needs an update" until the person updates.
   */
  needsUpdate?: boolean;
  error?: string;
  /** Kept once built, so the service worker can send it (D68). */
  request?: QueuedRequest;
  /**
   * A new poll (M60, D220); `content` is its question. Sealed when its
   * request is built, like text.
   */
  poll?: PollDraft;
  /**
   * A new gathering (M71, D282); `content` is its title. Sealed when its
   * request is built, like a poll.
   */
  gathering?: GatheringDraft;
  ownerId?: string;
}

export const outboxAtom = atom<OutboxEntry[]>([]);

/** A gathering on its way (M71, D282); its title is the entry's content. */
export interface GatheringDraft {
  place?: string;
  note?: string;
  /** ISO time it starts; the server refuses one long past or too far ahead. */
  startsAt: string;
  rings: boolean;
}

/**
 * "Come now" for a gathering (M71, D286), from gathering:ring: the
 * full-screen ring shows while it's set (Task 8 of the plan clears it).
 */
export const gatheringRingAtom = atom<WsGatheringRingPayload | null>(null);

/** A poll on its way (M60, D220). */
export interface PollDraft {
  options: string[];
  multiple: boolean;
  /** From when the server receives it (D220); null: never. */
  closesInSeconds: number | null;
}
